import i18n from '../../i18n';

// Base API Error class
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// Network error for connection issues
export class NetworkError extends ApiError {
  constructor(
    message?: string,
    public originalError?: Error,
    details?: Record<string, unknown>
  ) {
    super(
      0,
      message || i18n.t('errors.network.connectionFailed'),
      'NETWORK_ERROR',
      details
    );
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

// CORS error for cross-origin resource sharing issues
export class CorsError extends NetworkError {
  constructor(message?: string, details?: Record<string, unknown>) {
    super(message || i18n.t('errors.network.corsError'), undefined, details);
    this.name = 'CorsError';
    this.code = 'CORS_ERROR';
    Object.setPrototypeOf(this, CorsError.prototype);
  }
}

// Timeout error for request timeouts
export class TimeoutError extends NetworkError {
  constructor(
    message?: string,
    public timeoutMs?: number,
    details?: Record<string, unknown>
  ) {
    super(message || i18n.t('errors.network.timeout'), undefined, details);
    this.name = 'TimeoutError';
    this.code = 'TIMEOUT_ERROR';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

// Authentication error for 401 responses
export class AuthenticationError extends ApiError {
  constructor(message?: string, details?: Record<string, unknown>) {
    super(
      401,
      message || i18n.t('errors.auth.failed'),
      'AUTHENTICATION_ERROR',
      details
    );
    this.name = 'AuthenticationError';
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

// Validation error for 400 responses
export class ValidationError extends ApiError {
  constructor(
    message?: string,
    public validationErrors?: Record<string, string[]>,
    details?: Record<string, unknown>
  ) {
    super(
      400,
      message || i18n.t('errors.general.validationFailed'),
      'VALIDATION_ERROR',
      details
    );
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

// Forbidden error for 403 responses
export class ForbiddenError extends ApiError {
  constructor(message?: string, details?: Record<string, unknown>) {
    super(
      403,
      message || i18n.t('errors.status.403'),
      'FORBIDDEN_ERROR',
      details
    );
    this.name = 'ForbiddenError';
    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

// Not found error for 404 responses
export class NotFoundError extends ApiError {
  constructor(message?: string, details?: Record<string, unknown>) {
    super(
      404,
      message || i18n.t('errors.network.notFound'),
      'NOT_FOUND',
      details
    );
    this.name = 'NotFoundError';
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

// Server error for 5xx responses
export class ServerError extends ApiError {
  constructor(
    status: number = 500,
    message?: string,
    details?: Record<string, unknown>
  ) {
    super(
      status,
      message || i18n.t('errors.network.serverError'),
      'SERVER_ERROR',
      details
    );
    this.name = 'ServerError';
    Object.setPrototypeOf(this, ServerError.prototype);
  }
}

// Rate limit error for 429 responses
export class RateLimitError extends ApiError {
  constructor(
    message?: string,
    public retryAfter?: number,
    details?: Record<string, unknown>
  ) {
    super(
      429,
      message || i18n.t('errors.network.rateLimitExceeded'),
      'RATE_LIMIT',
      details
    );
    this.name = 'RateLimitError';
    Object.setPrototypeOf(this, RateLimitError.prototype);
  }
}

// Helper function to get default status text
function getStatusText(status: number): string {
  switch (status) {
    case 400:
      return i18n.t('errors.status.400');
    case 401:
      return i18n.t('errors.status.401');
    case 403:
      return i18n.t('errors.status.403');
    case 404:
      return i18n.t('errors.status.404');
    case 429:
      return i18n.t('errors.status.429');
    case 500:
      return i18n.t('errors.status.500');
    case 502:
      return i18n.t('errors.status.502');
    case 503:
      return i18n.t('errors.status.503');
    default:
      if (status >= 400 && status < 500) {
        return i18n.t('errors.status.clientError');
      } else if (status >= 500) {
        return i18n.t('errors.status.serverError');
      }
      return i18n.t('errors.status.generic');
  }
}

// Helper function to extract error message from various response formats
function extractErrorMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const errorData = data as Record<string, unknown>;

  // Check the most common error message fields only
  const messageFields = ['message', 'detail', 'error'];

  // Check direct message fields
  for (const field of messageFields) {
    if (typeof errorData[field] === 'string' && errorData[field]) {
      return errorData[field] as string;
    }
  }

  // Check nested error object (common pattern)
  if (typeof errorData.error === 'object' && errorData.error !== null) {
    const nestedError = errorData.error as Record<string, unknown>;
    if (typeof nestedError.message === 'string' && nestedError.message) {
      return nestedError.message;
    }
  }

  // Check for errors array (common in validation responses)
  if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
    const firstError = errorData.errors[0];
    if (typeof firstError === 'string') {
      return firstError;
    }
    if (
      typeof firstError === 'object' &&
      firstError !== null &&
      'message' in firstError &&
      typeof firstError.message === 'string'
    ) {
      return firstError.message;
    }
  }

  return null;
}

// Helper function to create appropriate error based on status code
export function createApiError(
  errorOrStatus:
    | number
    | { status: number; data?: unknown; statusText?: string },
  message?: string,
  data?: unknown
): ApiError {
  // Handle case where first argument is an error response object
  if (
    typeof errorOrStatus === 'object' &&
    errorOrStatus !== null &&
    'status' in errorOrStatus
  ) {
    const errorResponse = errorOrStatus as {
      status: number;
      data?: unknown;
      statusText?: string;
    };
    const extractedMessage =
      extractErrorMessage(errorResponse.data) ||
      errorResponse.statusText ||
      message ||
      i18n.t('errors.general.unknownError');
    return createApiError(
      errorResponse.status,
      extractedMessage,
      errorResponse.data
    );
  }

  // Handle regular status code case
  const status = errorOrStatus as number;
  const finalMessage =
    message || getStatusText(status) || i18n.t('errors.general.unknownError');
  const details =
    typeof data === 'object' && data !== null
      ? (data as Record<string, unknown>)
      : undefined;

  switch (status) {
    case 0:
      return new NetworkError(finalMessage, undefined, details);
    case 400:
      return new ValidationError(
        finalMessage,
        details?.errors as Record<string, string[]>,
        details
      );
    case 401:
      return new AuthenticationError(finalMessage, details);
    case 403:
      return new ForbiddenError(finalMessage, details);
    case 404:
      return new NotFoundError(finalMessage, details);
    case 429:
      return new RateLimitError(
        finalMessage,
        details?.retryAfter as number,
        details
      );
    default:
      if (status >= 500) {
        return new ServerError(status, finalMessage, details);
      }
      return new ApiError(status, finalMessage, undefined, details);
  }
}

// Retry utility with exponential backoff
export interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  factor?: number;
  shouldRetry?: (error: Error, attempt: number) => boolean;
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    factor = 2,
    shouldRetry = error => {
      // Explicitly handle specific error types
      if (error instanceof NetworkError) {
        return true; // Always retry network errors
      }
      if (error instanceof ServerError) {
        return true; // Always retry server errors (5xx)
      }
      if (error instanceof RateLimitError) {
        return true; // Retry rate limit errors
      }
      if (error instanceof AuthenticationError) {
        return false; // Never retry authentication errors
      }
      if (error instanceof ForbiddenError) {
        return false; // Never retry forbidden errors
      }
      if (error instanceof ValidationError) {
        return false; // Never retry validation errors
      }
      if (error instanceof NotFoundError) {
        return false; // Never retry 404 errors
      }

      // Fallback for generic ApiError
      if (error instanceof ApiError) {
        // Don't retry client errors (4xx) except those already handled above
        if (error.status >= 400 && error.status < 500) {
          return false;
        }
        // Retry server errors (5xx)
        return error.status >= 500;
      }

      // For non-API errors, retry by default
      return true;
    },
  } = options;

  let lastError: Error | null = null;
  const delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (attempt === maxRetries || !shouldRetry(lastError, attempt)) {
        throw lastError;
      }

      // Calculate next delay with exponential backoff
      const currentDelay = Math.min(
        delay * Math.pow(factor, attempt),
        maxDelay
      );

      // Add jitter to prevent thundering herd
      const jitter = Math.random() * 0.1 * currentDelay;
      const finalDelay = currentDelay + jitter;

      await new Promise(resolve => setTimeout(resolve, finalDelay));
    }
  }

  throw lastError || new Error(i18n.t('errors.network.retryFailed'));
}
